import { z } from 'zod';
import { DayHours, ErrorBody, Identity, IsoDateTime, OfferedSlot } from '@yourstore/domain';

/**
 * The guest wire, as this bundle understands it.
 *
 * Mirrored by hand from `apps/guest-api/src/routes/*.ts`, for the reason
 * `apps/app/src/wire.ts` gives: apps do not cross-import (dev.md §3), and the
 * server's route modules pull in `@yourstore/store` — Drizzle and the `postgres`
 * driver — which must never reach a browser bundle. What the two sides *do*
 * share is `@yourstore/domain`, so the scalars that carry a promise
 * (`IsoDateTime` with an explicit offset, `Identity`, `OfferedSlot`,
 * `ErrorBody`'s closed code set) are one definition rather than two lookalikes.
 *
 * Every response is parsed through these before it reaches a component. On this
 * surface that matters twice over: the SPA is **one static build for every
 * tenant hostname** (spec §6.4.2), so a shape it cannot parse is a shape it will
 * meet on somebody's live domain, and the honest failure is a sentence on the
 * page rather than a `TypeError` in a stranger's console.
 *
 * Where a schema is looser than it looks like it should be — `branding` is
 * `unknown`, `details` is a bare `string[]` — the looseness is the point and is
 * noted where it appears.
 */

/* ------------------------------------------------------------------ */
/* GET /api/site — "who am I", answered from the Host header           */
/* ------------------------------------------------------------------ */

/**
 * The tenant's own facts. `bookable` is the *state*, not the slug: `guest-api`
 * projects `status ∈ {live, importing}` and deliberately never sends the tenant
 * id, because nothing on a public page needs it.
 */
export const SiteInfo = z.object({
  name: z.string(),
  timezone: z.string(),
  currency: z.string(),
  bookable: z.boolean(),
  /**
   * `tenant_config.config.branding`, verbatim. Untyped **here** on purpose: the
   * theme contract (`src/theme/`) is the thing that knows what a branding
   * document may contain, and it validates version-aware, ignoring what it does
   * not recognise rather than refusing the whole site. A strict schema at this
   * layer would turn "the admin console published a token this bundle is one
   * release behind on" into a blank page.
   */
  branding: z.record(z.string(), z.unknown()).default({}),
  /**
   * The tenant's opening hours as the booking engine has them — seven
   * `DayHours`, Monday-first, wall-clock in `timezone` above, multi-window so a
   * restaurant's split shift arrives as two windows rather than as the envelope
   * around both.
   *
   * **Additive, and defensively so.** It is absent from every response this
   * bundle met before the projection landed, and it may be absent again from a
   * tenant that has published no week — so it defaults to `[]`, and `.catch`
   * turns a malformed day into "no hours published" instead of into a blank
   * page on somebody's live domain. `[]` is what makes the hours block fall
   * back to the rows the owner typed, which is the behaviour that existed
   * before this field did.
   */
  hours: z.array(DayHours).catch([]).default([]),
  catalog_version: z.number().int(),
});
export type SiteInfo = z.infer<typeof SiteInfo>;

export { DayHours };

/**
 * One sellable service, as the guest catalog projects it.
 *
 * `price_cents` is here because **the catalog is where a price comes from** —
 * the same row `createBooking` reads (obligation B7, generalized). The SPA
 * displays this number and never sends one back; `CreateBooking` below has no
 * field for it, which is what makes that a structural fact rather than a rule
 * somebody remembers.
 *
 * No `resource_id`s: `guest-api` withholds them from this projection, so the
 * only resource the SPA ever names is one an availability answer just offered.
 */
export const SiteService = z.object({
  service_id: z.string(),
  name: z.string(),
  /** `items.description` is `NOT NULL DEFAULT ''` — an absent one is `''`. */
  description: z.string(),
  duration_min: z.number().int(),
  max_party: z.number().int(),
  price_cents: z.number().int(),
  details: z.array(z.string()),
});
export type SiteService = z.infer<typeof SiteService>;

/**
 * What the site tells a browser about its own session — never the cookie.
 *
 * The cookie is `httpOnly` (`guest-api/src/session.ts`); this bundle cannot read
 * it and does not try. "Am I signed in" is a server answer, carried here and on
 * `/api/me`, and every request simply travels with `credentials: 'include'`.
 */
export const SessionSummary = z.object({
  authenticated: z.boolean(),
  expires_at: z.string().nullable(),
});
export type SessionSummary = z.infer<typeof SessionSummary>;

export const SiteResponse = z.object({
  site: SiteInfo,
  services: z.array(SiteService),
  session: SessionSummary,
});
export type SiteResponse = z.infer<typeof SiteResponse>;

/* ------------------------------------------------------------------ */
/* GET /api/availability — the one engine, asked by the guest           */
/* ------------------------------------------------------------------ */

/**
 * `packages/scheduling`'s `AvailabilityResult`, unchanged.
 *
 * `alternatives` is non-empty whenever the ask did not fit and the horizon holds
 * anything, and the SPA is required to *show* it: "no times that day" with a
 * ladder of nearby days sitting unread in the response would be this surface's
 * version of the failure the agreement calls out as mattering most.
 */
export const Availability = z.object({
  authority: z.enum(['live', 'approximate']),
  slots: z.array(OfferedSlot),
  alternatives: z.array(OfferedSlot),
  summary: z.string(),
});
export type Availability = z.infer<typeof Availability>;

export { OfferedSlot };

/* ------------------------------------------------------------------ */
/* Claim-by-proof — POST /api/otp/request, POST /api/otp/verify         */
/* ------------------------------------------------------------------ */

/**
 * `202`, and the same body whether the identity is known or not (spec §6.4.1).
 * The SPA must never draw a distinction the server refused to draw, so there is
 * nothing here to branch on: a message to repeat and a countdown to run.
 */
export const OtpRequested = z.object({
  status: z.literal('sent'),
  message: z.string(),
  expires_in_seconds: z.number().int(),
});
export type OtpRequested = z.infer<typeof OtpRequested>;

export const OtpVerified = z.object({ session: SessionSummary });
export type OtpVerified = z.infer<typeof OtpVerified>;

/* ------------------------------------------------------------------ */
/* GET /api/me                                                          */
/* ------------------------------------------------------------------ */

/**
 * A name and the identities this guest has proved. Nothing else exists on the
 * guest surface to ask for — no preferences, no notes, no balances (§6.4) — and
 * the absence is enforced by grants in migration 0010, not by this schema.
 */
export const Me = z.object({
  guest: z.object({
    display_name: z.string(),
    identities: z.array(Identity),
  }),
  session: SessionSummary,
});
export type Me = z.infer<typeof Me>;

export const SessionEnded = z.object({ session: SessionSummary });

/* ------------------------------------------------------------------ */
/* The book — /api/bookings                                             */
/* ------------------------------------------------------------------ */

/**
 * One booking, in the single shape `serialize()` in `routes/bookings.ts`
 * produces for both the create and the list, so the two cannot drift here
 * either.
 *
 * `value_cents` is the catalog's number snapshotted at booking time. It arrives;
 * it never departs.
 *
 * `status` is a bare string rather than the staff surface's enum: `guest-api`
 * passes `appointments.status` straight through, and a guest looking at a
 * visit's status must not meet a blank page because the server learned a sixth
 * word. The UI knows `confirmed` and `cancelled` and prints anything else as it
 * came.
 */
export const Booking = z.object({
  booking_id: z.string(),
  start: IsoDateTime,
  end: IsoDateTime,
  service_id: z.string(),
  service_name: z.string(),
  party_size: z.number().int(),
  status: z.string(),
  value_cents: z.number().int(),
  /** Echoed by `withIntentId` when the request carried one. */
  client_intent_id: z.string().optional(),
});
export type Booking = z.infer<typeof Booking>;

export const BookingList = z.object({ bookings: z.array(Booking) });
export type BookingList = z.infer<typeof BookingList>;

export const BookingCancelled = z.object({
  booking_id: z.string(),
  status: z.string(),
  fee_cents: z.number().int(),
  client_intent_id: z.string().optional(),
});
export type BookingCancelled = z.infer<typeof BookingCancelled>;

/**
 * The create body — **and the shape of obligation B7 on this surface.**
 *
 * There is no `price_cents` and no `end`. Not "rejected": absent. The server
 * takes the price and the duration from `item_scheduling` and would drop a price
 * silently if one were sent, so the client that cannot construct one is the
 * client that cannot get it wrong. `site.test.ts`'s sibling here
 * (`ui/booking.test.tsx`) inspects the POST body and fails if money appears in
 * it.
 */
export const CreateBooking = z.object({
  service_id: z.string(),
  /** Only ever a `resource_id` an availability answer just offered. */
  resource_id: z.string(),
  start: IsoDateTime,
  party_size: z.number().int().min(1).max(50).optional(),
  client_intent_id: z.string().optional(),
});
export type CreateBooking = z.infer<typeof CreateBooking>;

export { ErrorBody, Identity, IsoDateTime };
